/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file solidGamingSlice.js
 * @description Redux slice for managing solidGaming related state and operations
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";

/**
 * @function getInitialState
 * @description Creates the initial state object for the solidGaming slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = (gameList = null) => ({
  error: null,
  gameList,
  loading: false,
});

/**
 * @function getSolidGamingGameList
 * @description Async thunk for get solid gaming game list operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getSolidGamingGameList = createAsyncThunk("solidGaming/loadGameList", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, { authToken, language });

    const result = await axios.get(`/player/casino/list/sg?originId=${originId}&lineId=${lineId}`);

    return {
      gameList: result.data,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain the Solid Gaming game List", // serializable (err.response.data)
      name: "Solid Gaming game list fetchError",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @constant solidGamingSlice
 * @description Redux slice for solidGaming management
 */
const solidGamingSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getSolidGamingGameList.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getSolidGamingGameList.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(getSolidGamingGameList.fulfilled, (state, action) => {
        state.gameList = action.payload.gameList;
        state.error = null;
        state.loading = false;
      });
  },
  initialState: getInitialState(),
  name: "solidGaming",
  reducers: {},
});
const { reducer } = solidGamingSlice;

export default reducer;
